'use client'

import { useEffect } from "react";

type GlobalErrorProps = {
  error: Error & { digest?: string };
  reset: () => void;
};

/**
 * Root-layout error boundary. Self-contained (includes html/body) with inline
 * styles ported from /error/500.html so it still renders if the app layout fails.
 */
export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    console.error("[Global Error]", error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="noindex" />
        <title>ERROR 500 - Internal Server Error | CloudAxe</title>
        <style>{`
          *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
          html, body { height: 100%; }
          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #111827;
            background: #ffffff;
            -webkit-font-smoothing: antialiased;
          }
          .layout { display: flex; min-height: 100vh; background: #ffffff; }
          .content-panel {
            flex: 1 1 66.666%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 3rem 2rem;
          }
          @media (min-width: 640px) { .content-panel { padding: 3rem; } }
          .content-inner { width: 100%; max-width: 28rem; margin: 0 auto; }
          .brand-header {
            display: flex; flex-direction: column; align-items: center;
            gap: 0.75rem; margin-bottom: 2rem;
          }
          @media (min-width: 640px) {
            .brand-header { flex-direction: row; align-items: center; gap: 0.75rem; }
          }
          .brand-logo-sm { width: 3rem; height: 3rem; flex-shrink: 0; object-fit: contain; }
          .brand-name {
            font-size: 1.25rem; font-weight: 600; letter-spacing: -0.025em; color: #000000;
          }
          @media (min-width: 640px) { .brand-name { font-size: 1.5rem; } }
          .error-badge {
            display: inline-block; font-size: 0.75rem; font-weight: 600;
            letter-spacing: 0.05em; text-transform: uppercase;
            padding: 0.375rem 0.75rem; border-radius: 9999px; margin-bottom: 1rem;
            background: #fffbeb; color: #b45309; border: 1px solid #fde68a;
          }
          h1 {
            font-size: 1.75rem; font-weight: 700; line-height: 1.25;
            margin-bottom: 1rem; color: #111827;
          }
          @media (min-width: 640px) { h1 { font-size: 2rem; } }
          h2 {
            font-size: 1rem; font-weight: 500; color: #374151; margin-bottom: 0.75rem;
          }
          .message {
            font-size: 0.9375rem; line-height: 1.6; color: #4b5563; margin-bottom: 0.75rem;
          }
          .note { font-size: 0.875rem; line-height: 1.6; color: #6b7280; }
          .actions { margin-top: 2rem; display: flex; flex-wrap: wrap; gap: 0.75rem; }
          .btn {
            display: inline-flex; align-items: center; justify-content: center;
            border-radius: 9999px; padding: 0.625rem 1.25rem;
            font-size: 0.875rem; font-weight: 600; cursor: pointer; text-decoration: none;
          }
          .btn-primary { background: #0f172a; color: #ffffff; border: none; }
          .btn-primary:hover { background: #1e293b; }
          .btn-secondary {
            background: #ffffff; color: #0f172a; border: 1px solid #cbd5e1;
          }
          .btn-secondary:hover { background: #f8fafc; }
          .page-footer {
            margin-top: 2.5rem; padding-top: 1.5rem; border-top: 1px solid #e5e7eb;
            font-size: 0.8125rem; color: #9ca3af; text-align: center;
          }
          .page-footer a { color: #1e3a8a; text-decoration: none; font-weight: 500; }
          .page-footer a:hover { text-decoration: underline; }
          .brand-panel {
            display: none; flex: 0 0 33.333%; position: relative;
            overflow: hidden; background: #ffffff; border-radius: 1.5rem 0 0 1.5rem;
          }
          @media (min-width: 1024px) { .brand-panel { display: flex; } }
          .brand-panel-inner {
            position: relative; display: flex; align-items: center;
            justify-content: center; width: 100%; min-height: 400px; overflow: hidden;
          }
          .brand-logo-lg {
            width: 220px; height: 220px; object-fit: contain; opacity: 0.6;
            animation: float-slow 24s ease-in-out infinite;
          }
          @keyframes float-slow {
            0%, 100% { transform: translateY(40%) scale(2.2) rotate(-12deg); }
            50% { transform: translateY(-40%) scale(2.2) rotate(-12deg); }
          }
        `}</style>
      </head>
      <body>
        <div className="layout">
          <div className="content-panel">
            <div className="content-inner">
              <div className="brand-header">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/hosting-control-panel/images/cloudaxe_onlyLogo_Transparent_NoBuffer.png"
                  alt="CloudAxe"
                  className="brand-logo-sm"
                />
                <span className="brand-name">CloudAxe</span>
              </div>
              <span className="error-badge">Error 500</span>
              <h1>Internal Server Error</h1>
              <h2>The following error occurred:</h2>
              <p className="message">
                The requested URL caused an internal server error.
              </p>
              <p className="note">
                If you get this message repeatedly please contact support@cloudaxe.io.
              </p>
              <div className="actions">
                <button type="button" className="btn btn-primary" onClick={() => reset()}>
                  Try Again
                </button>
                <a href="/" className="btn btn-secondary">
                  Return Home
                </a>
              </div>
              <div className="page-footer">
                <p>
                  Hosted by <a href="/">CloudAxe</a>
                </p>
              </div>
            </div>
          </div>
          <aside className="brand-panel" aria-hidden="true">
            <div className="brand-panel-inner">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/hosting-control-panel/images/cloudaxe_onlyLogo_Transparent_NoBuffer.png"
                alt=""
                className="brand-logo-lg"
              />
            </div>
          </aside>
        </div>
      </body>
    </html>
  );
}
